// Google Analytics 4 utility for DropService.in
// Tracks: signup flow, payment success/failure, affiliate conversions, dashboard engagement

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

/**
 * Track a custom GA4 event
 */
export function trackEvent(
  eventName: string,
  params: Record<string, string | number | boolean> = {}
) {
  if (typeof window === 'undefined') return;
  if (!GA_ID) return;
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
}

// ── Signup / Auth Events ──────────────────────────────────────────────────────

export function trackSignupStarted() {
  trackEvent('signup_started', { event_category: 'auth' });
}

export function trackSignupPaymentInitiated(amount: number) {
  trackEvent('signup_payment_initiated', {
    event_category: 'auth',
    currency: 'INR',
    value: amount,
  });
}

export function trackSignupCompleted(userId: string) {
  trackEvent('sign_up', {
    event_category: 'auth',
    method: 'email',
    user_id: userId,
  });
}

export function trackLogin(userId: string) {
  trackEvent('login', {
    event_category: 'auth',
    method: 'email',
    user_id: userId,
  });
}

// ── Payment Events ────────────────────────────────────────────────────────────

export function trackPaymentSuccess(orderId: string, amount: number) {
  trackEvent('purchase', {
    event_category: 'payment',
    transaction_id: orderId,
    currency: 'INR',
    value: amount,
  });
}

export function trackPaymentFailed(orderId: string) {
  trackEvent('payment_failed', {
    event_category: 'payment',
    transaction_id: orderId,
  });
}

// ── Affiliate / Conversion Events ─────────────────────────────────────────────

export function trackAffiliateReferralCopied(referralCode: string) {
  trackEvent('affiliate_link_copied', {
    event_category: 'affiliate',
    referral_code: referralCode,
  });
}

export function trackAffiliateConversion(referrerId: string, newUserId: string) {
  trackEvent('affiliate_conversion', {
    event_category: 'affiliate',
    referrer_id: referrerId,
    new_user_id: newUserId,
  });
}

export function trackMilestoneClaimed(milestoneCount: number, reward: number) {
  trackEvent('milestone_claimed', {
    event_category: 'affiliate',
    milestone_count: milestoneCount,
    reward_amount: reward,
  });
}

// ── Dashboard Engagement Events ───────────────────────────────────────────────

export function trackDashboardViewed(userId: string) {
  trackEvent('dashboard_viewed', {
    event_category: 'engagement',
    user_id: userId,
  });
}

export function trackOrderPlaced(orderId: string, serviceType: string, amount: number) {
  trackEvent('order_placed', {
    event_category: 'engagement',
    order_id: orderId,
    service_type: serviceType,
    value: amount,
    currency: 'INR',
  });
}

export function trackWalletTopup(amount: number) {
  trackEvent('wallet_topup', {
    event_category: 'engagement',
    value: amount,
    currency: 'INR',
  });
}
